import React, { useState, useEffect } from 'react';
import { Lock, ChevronDown, Edit2, Trash2, Plus, X, Download } from 'lucide-react';
import { leadAPIApi, productsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { message } from 'antd';
import Pagination from '../components/Pagination';
import ExcelExportButton from '../components/ExcelExportButton';

// Dropdown options
const SOURCE_TYPE_OPTIONS = ['Select source type', 'MANUAL', 'WEBSITE', 'LANDING_PAGE'];
const DAYS_PERMISSION_OPTIONS = ['Not Allowed', '7', '15', '30', '45', '60'];

const CAMPAIGN_OPTIONS = [
  'Please select', 'F-Lead', 'RTO-Lead', 'RTO Dialer', 'Renewal-Lead', 'Renewal Dialer',
  'Blaster', 'Customer Service', 'OnBoarding', 'Upsell', 'Verification', 'NDR', 'OJT',
  'OnBoarding_2', 'RD1_1', 'RD1_2', 'RD1_3+', 'RD2_1', 'RD2_2+', 'RD3', 'Piles', 'Alimul',
  'Irfan', 'Sahil',
];

const CATEGORY_OPTIONS = [
  'Please select', 'Allopathic Medicine', 'Allopathic Men Wellness', 'Allopathic Women Wellness',
  'Ayurvedic Bone', 'Ayurvedic BP', 'Ayurvedic Brain Support Kids', 'Ayurvedic Cough',
  'Ayurvedic Diabetes', 'Ayurvedic Joint Pain', 'Ayurvedic Kedny', 'Ayurvedic Liver',
  'Ayurvedic Medicine', 'Ayurvedic Men Wellness', 'Ayurvedic Oral Protection', 'Ayurvedic Piles',
  'Ayurvedic Psoriasis', 'Ayurvedic Teeth', 'Ayurvedic Thyroid', 'Ayurvedic Wellness',
  'Ayurvedic Wight Loss', 'Ayurvedic Women Wellness', 'Cosmetic', 'Cosmetic Face Skin',
  'Cosmetic Hair', 'Cosmetic Skin', 'Hair care',
];

function Toggle({ checked, onChange, ariaLabel }) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 border-2`}
      style={{
        backgroundColor: checked ? currentTheme.color : '#FFFFFF',
        borderColor: isDark ? '#FFFFFF' : '#D1D5DB',
        '--tw-ring-color': currentTheme.color
      }}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full transition-transform ${checked ? 'translate-x-5 bg-white' : 'translate-x-1 bg-gray-400'
          } border-2 border-white shadow-sm`}
      />
    </button>
  );
}

/**
 * Generates the next available Lead Code
 */
function getNextLeadCode(existingRows) {
  if (!existingRows || existingRows.length === 0) return '1001';
  const max = existingRows.reduce((acc, r) => {
    const n = Number(r.leadCode);
    return Number.isFinite(n) ? Math.max(acc, n) : acc;
  }, 1000);
  return String(max + 1);
}

/**
 * Main Component
 */
export default function SettingsLeadApi() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  // State management
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [addForm, setAddForm] = useState({
    leadSourceName: '',
    campaign: 'Please select',
    category: 'Please select',
    leadCode: '1001',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch lead APIs, campaigns, and categories in parallel
      const [leadAPIsData, campaignsData, categoriesData] = await Promise.all([
        leadAPIApi.getAllLeadAPIs(),
        leadAPIApi.getAllCampaigns(),
        productsApi.getActiveProductCategories().catch(() => [])
      ]);

      // Handle response structure depending on whether it comes as array or object
      const apiList = Array.isArray(leadAPIsData) ? leadAPIsData : (leadAPIsData.content || []);
      const campaignList = Array.isArray(campaignsData) ? campaignsData : (campaignsData.content || []);
      
      const catList = Array.isArray(categoriesData) ? categoriesData : (categoriesData.data || []);
      const categoryNames = catList.map(c => c.categoryName || c.name || c);

      // Transform API response to match UI format
      const transformedRows = apiList.map((item) => ({
        id: item.id.toString(),
        leadCode: item.leadCode,
        leadSource: item.leadSource,
        leadSourceType: item.leadSourceType || 'Select source type',
        campaign: item.campaignName || '',
        campaignId: item.campaignId,
        category: item.category,
        duplicatePermission: item.duplicatePermission,
        daysPermission: item.daysPermission ? item.daysPermission.toString() : 'Not Allowed',
        status: item.status,
      }));

      setRows(transformedRows);
      setCampaigns(campaignList);
      setCategories(categoryNames);

      // Update next lead code based on fetched rows
      if (transformedRows.length > 0) {
        setAddForm(prev => ({
          ...prev,
          leadCode: getNextLeadCode(transformedRows)
        }));
      }

    } catch (err) {
      console.error('Error fetching data:', err);
      // In case of error (e.g. backend not running), keep loading false so UI shows up empty or with error
      setError(err.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle adding new lead API
   */
  const handleAddSubmit = async () => {
    try {
      // Validate form
      if (!addForm.leadSourceName.trim()) {
        message.warning('Please enter a lead source name');
        return;
      }
      if (addForm.campaign === 'Please select') {
        message.warning('Please select a campaign');
        return;
      }
      if (addForm.category === 'Please select') {
        message.warning('Please select a category');
        return;
      }

      // Find campaign ID
      const selectedCampaign = campaigns.find(c => c.campaignName === addForm.campaign);

      // Get current user ID (mock or from auth)
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const createdById = currentUser.id || 1;

      // Prepare data for API
      const apiData = {
        leadCode: addForm.leadCode,
        leadSource: addForm.leadSourceName,
        leadSourceType: 'MANUAL',
        campaignId: selectedCampaign?.id || null, // Ensure ID is sent
        category: addForm.category,
        duplicatePermission: true,
        daysPermission: 0, // "Not Allowed" = 0
        status: true,
        createdById: createdById,
      };

      // Call API
      await leadAPIApi.createLeadAPI(apiData);

      // Refresh data
      await fetchData();

      // Close modal and reset form
      setIsAddOpen(false);
      setAddForm({
        leadSourceName: '',
        campaign: 'Please select',
        category: 'Please select',
        leadCode: '', // Will be updated by fetchData logic
      });

      message.success('Lead API created successfully!');
    } catch (err) {
      console.error('Error creating lead API:', err);
      message.error(err.message || 'Failed to create lead API. Please try again.');
    }
  };

  /**
   * Handle editing lead API
   */
  const handleEditSubmit = async () => {
    try {
      if (!editingRow) return;

      // Find campaign ID
      const selectedCampaign = campaigns.find(c => c.campaignName === editingRow.campaign);

      // Prepare data for API
      const apiData = {
        leadCode: editingRow.leadCode,
        leadSource: editingRow.leadSource,
        leadSourceType: editingRow.leadSourceType === 'Select source type' ? null : editingRow.leadSourceType,
        campaignId: selectedCampaign?.id || editingRow.campaignId,
        category: editingRow.category,
        duplicatePermission: editingRow.duplicatePermission,
        daysPermission: editingRow.daysPermission === 'Not Allowed' ? 0 : parseInt(editingRow.daysPermission),
        status: editingRow.status,
      };

      // Call API
      await leadAPIApi.updateLeadAPI(editingRow.id, apiData);

      // Refresh data
      await fetchData();

      // Close modal
      setIsEditOpen(false);
      setEditingRow(null);

      message.success('Lead API updated successfully!');
    } catch (err) {
      console.error('Error updating lead API:', err);
      message.error(err.message || 'Failed to update lead API. Please try again.');
    }
  };

  /**
   * Handle inline updates (toggles and dropdowns)
   */
  const handleInlineUpdate = async (rowId, patch) => {
    try {
      const row = rows.find(r => r.id === rowId);
      if (!row) return;

      // Update local state immediately for better UX
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r)));

      // Find campaign ID if needed
      let campaignId = row.campaignId;
      if (patch.campaign) {
        const selectedCampaign = campaigns.find(c => c.campaignName === patch.campaign);
        campaignId = selectedCampaign?.id;
      }

      // Prepare data for API
      const apiData = {
        leadCode: row.leadCode,
        leadSource: row.leadSource,
        leadSourceType: patch.leadSourceType || row.leadSourceType,
        campaignId: campaignId,
        category: row.category,
        duplicatePermission: patch.duplicatePermission !== undefined ? patch.duplicatePermission : row.duplicatePermission,
        daysPermission: patch.daysPermission
          ? (patch.daysPermission === 'Not Allowed' ? 0 : parseInt(patch.daysPermission))
          : (row.daysPermission === 'Not Allowed' ? 0 : parseInt(row.daysPermission)),
        status: patch.status !== undefined ? patch.status : row.status,
      };

      // Call API
      await leadAPIApi.updateLeadAPI(rowId, apiData);
    } catch (err) {
      console.error('Error updating lead API:', err);
      // Revert local state or refresh
      fetchData();
    }
  };

  /**
   * Handle delete
   */
  const handleDelete = async (rowId) => {
    if (!window.confirm('Are you sure you want to delete this lead API?')) {
      return;
    }

    try {
      await leadAPIApi.deleteLeadAPI(rowId);
      await fetchData();
      message.success('Lead API deleted successfully!');
    } catch (err) {
      console.error('Error deleting lead API:', err);
      message.error(err.message || 'Failed to delete lead API. Please try again.');
    }
  };

  /**
   * Open edit modal
   */
  const openEditModal = (row) => {
    setEditingRow({ ...row });
    setIsEditOpen(true);
  };

  /**
   * Handle downloading Generic PDF Data (Calls Generic API)
   */
  const handleDownloadAllData = async () => {
    try {
      setLoading(true);
      const response = await leadAPIApi.downloadIntegrationGuide('generic');

      if (!response.ok) throw new Error('Failed to download PDF');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Generic_Integration_Data.pdf');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success('Generic Data PDF downloaded successfully!');
    } catch (err) {
      console.error('Error downloading generic PDF:', err);
      message.error('Failed to download PDF. Please check connection.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle downloading Specific Row PDF Data
   */
  const handleDownloadRowData = async (row) => {
    try {
      setLoading(true);
      const response = await leadAPIApi.downloadIntegrationGuide(row.leadCode);

      if (!response.ok) throw new Error('Failed to download PDF');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Lead_API_Data_${row.leadCode}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Data PDF for ${row.leadCode} downloaded successfully!`);
    } catch (err) {
      console.error('Error downloading row PDF:', err);
      message.error('Failed to download PDF.');
    } finally {
      setLoading(false);
    }
  };

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-lg text-red-600">{error}</div>
        <button
          onClick={fetchData}
          className="px-6 py-2 rounded-lg text-white hover:opacity-90 transition-opacity"
          style={{ backgroundColor: currentTheme.color }}
        >
          Retry
        </button>
      </div>
    );
  }

  const campaignOptions = ['Please select', ...campaigns.map(c => c.campaignName)];
  const categoryOptions = ['Please select', ...categories];

  return (
    <div className={`space-y-4 relative min-h-screen -mx-8 px-8 pb-8 transition-colors duration-300 ${isDark ? 'bg-[#0f172a] text-white' : ''}`}>
      {/* Page Header Section */}
      <div className="sticky top-0 z-30 -mx-8 px-8 py-3" style={{ backgroundColor: `${currentTheme.color}10` }}>
        <div className="flex items-center justify-between">
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Lead API</h2>

          <div className="flex items-center gap-3">
            {/* Download Button */}
            <ExcelExportButton
              onClick={handleDownloadAllData}
              isDownloading={loading}
              label="DOWNLOAD PDF"
            />

            {/* Add New Lead API Button */}
            <button
              type="button"
              onClick={() => {
                // Update lead code before opening
                const nextCode = getNextLeadCode(rows);
                setAddForm(prev => ({
                  ...prev,
                  leadSourceName: '',
                  campaign: 'Please select',
                  category: 'Please select',
                  leadCode: nextCode
                }));
                setIsAddOpen(true);
              }}
              className="inline-flex h-12 w-12 items-center justify-center rounded-full text-white shadow-lg transition-colors"
              style={{ backgroundColor: currentTheme.color }}
              title="Add Lead API"
              aria-label="Add Lead API"
            >
              <Plus className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Add New Lead API Modal */}
      {isAddOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Add New Lead API"
          onClick={() => setIsAddOpen(false)}
        >
          <div
            className={`w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-white/50'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4" style={{ backgroundColor: `${currentTheme.color}15` }}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Add New Lead API</h3>
              <button
                type="button"
                onClick={() => setIsAddOpen(false)}
                className="p-2 rounded-lg transition-colors"
                style={{ color: currentTheme.color }}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5 pb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Lead Source Name</label>
                  <input
                    value={addForm.leadSourceName}
                    onChange={(e) => setAddForm((p) => ({ ...p, leadSourceName: e.target.value }))}
                    placeholder="Enter name"
                    className={`w-full h-10 px-3 border rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'border-gray-300 text-gray-900'}`}
                    style={{ '--tw-ring-color': currentTheme.color }}
                    onFocus={(e) => e.target.style.borderColor = currentTheme.color}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                  />
                </div>

                <div>
                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Select Campaign</label>
                  <div className="relative">
                    <select
                      value={addForm.campaign}
                      onChange={(e) => setAddForm((p) => ({ ...p, campaign: e.target.value }))}
                      className={`w-full h-10 px-3 pr-10 border rounded-lg text-sm focus:outline-none transition-all appearance-none ${isDark ? 'bg-[#0f172a] border-white/10 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                      style={{ '--tw-ring-color': currentTheme.color }}
                      onFocus={(e) => e.target.style.borderColor = currentTheme.color}
                      onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    >
                      {campaignOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" style={{ color: currentTheme.color }}>
                      <ChevronDown className="h-5 w-5" />
                    </span>
                  </div>
                </div>

                <div>
                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Select Category</label>
                  <div className="relative">
                    <select
                      value={addForm.category}
                      onChange={(e) => setAddForm((p) => ({ ...p, category: e.target.value }))}
                      className={`w-full h-10 px-3 pr-10 border rounded-lg text-sm focus:outline-none transition-all appearance-none ${isDark ? 'bg-[#0f172a] border-white/10 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                      style={{ '--tw-ring-color': currentTheme.color }}
                      onFocus={(e) => e.target.style.borderColor = currentTheme.color}
                      onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    >
                      {categoryOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" style={{ color: currentTheme.color }}>
                      <ChevronDown className="h-5 w-5" />
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Lead Code</label>
                  <div className="relative">
                    <input
                      value={addForm.leadCode}
                      readOnly
                      className={`w-full h-10 pr-10 px-3 border rounded-lg text-sm focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-gray-300' : 'bg-gray-100 border-gray-300 text-gray-700'}`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: currentTheme.color }}>
                      <Lock className="h-5 w-5" />
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsAddOpen(false)}
                  className={`px-6 py-2.5 rounded-xl border font-semibold transition-colors ${isDark ? 'border-white/10 text-gray-300 hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddSubmit}
                  className="px-6 py-2.5 rounded-xl text-white font-semibold transition-colors"
                  style={{ backgroundColor: currentTheme.color }}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Lead API Modal */}
      {isEditOpen && editingRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Edit Lead API"
          onClick={() => setIsEditOpen(false)}
        >
          <div
            className={`w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-white/50'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between px-6 py-4 ${isDark ? 'bg-white/5' : 'bg-teal-100'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Edit Lead API</h3>
              <button
                type="button"
                onClick={() => setIsEditOpen(false)}
                className="p-2 rounded-lg hover:bg-teal-200/60 transition-colors"
                aria-label="Close"
                title="Close"
              >
                <X className="h-5 w-5 text-gray-700" />
              </button>
            </div>

            <div className="px-6 py-5 pb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Lead Source Name</label>
                  <input
                    value={editingRow.leadSource}
                    onChange={(e) => setEditingRow((p) => ({ ...p, leadSource: e.target.value }))}
                    className={`w-full h-10 px-3 border rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    style={{ focusBorderColor: currentTheme.color }}
                  />
                </div>

                <div>
                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Lead Source Type</label>
                  <div className="relative">
                    <select
                      value={editingRow.leadSourceType}
                      onChange={(e) => setEditingRow((p) => ({ ...p, leadSourceType: e.target.value }))}
                      className={`w-full h-10 px-3 pr-10 border rounded-lg text-sm focus:outline-none transition-all appearance-none ${isDark ? 'bg-[#0f172a] border-white/10 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                      style={{ focusBorderColor: currentTheme.color }}
                    >
                      {SOURCE_TYPE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" style={{ color: currentTheme.color }}>
                      <ChevronDown className="h-5 w-5" />
                    </span>
                  </div>
                </div>

                <div>
                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Campaign</label>
                  <div className="relative">
                    <select
                      value={editingRow.campaign}
                      onChange={(e) => setEditingRow((p) => ({ ...p, campaign: e.target.value }))}
                      className={`w-full h-10 px-3 pr-10 border rounded-lg text-sm focus:outline-none transition-all appearance-none ${isDark ? 'bg-[#0f172a] border-white/10 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                      style={{ focusBorderColor: currentTheme.color }}
                    >
                      {campaignOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" style={{ color: currentTheme.color }}>
                      <ChevronDown className="h-5 w-5" />
                    </span>
                  </div>
                </div>

                <div>
                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Category</label>
                  <div className="relative">
                    <select
                      value={editingRow.category}
                      onChange={(e) => setEditingRow((p) => ({ ...p, category: e.target.value }))}
                      className={`w-full h-10 px-3 pr-10 border rounded-lg text-sm focus:outline-none transition-all appearance-none ${isDark ? 'bg-[#0f172a] border-white/10 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                      style={{ focusBorderColor: currentTheme.color }}
                    >
                      {categoryOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" style={{ color: currentTheme.color }}>
                      <ChevronDown className="h-5 w-5" />
                    </span>
                  </div>
                </div>

                <div>
                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Days Permission</label>
                  <div className="relative">
                    <select
                      value={editingRow.daysPermission}
                      onChange={(e) => setEditingRow((p) => ({ ...p, daysPermission: e.target.value }))}
                      className={`w-full h-10 px-3 pr-10 border rounded-lg text-sm focus:outline-none transition-all appearance-none ${isDark ? 'bg-[#0f172a] border-white/10 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                      style={{ focusBorderColor: currentTheme.color }}
                    >
                      {DAYS_PERMISSION_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-teal-600">
                      <ChevronDown className="h-5 w-5" />
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Lead Code</label>
                  <div className="relative">
                    <input
                      value={editingRow.leadCode}
                      readOnly
                      className={`w-full h-10 pr-10 px-3 border rounded-lg text-sm focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-gray-300' : 'bg-gray-100 border-gray-300 text-gray-700'}`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-teal-600">
                      <Lock className="h-5 w-5" />
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Duplicate Permission</label>
                  <Toggle
                    checked={editingRow.duplicatePermission}
                    onChange={(v) => setEditingRow((p) => ({ ...p, duplicatePermission: v }))}
                    ariaLabel="Toggle duplicate permission"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                  <Toggle
                    checked={editingRow.status}
                    onChange={(v) => setEditingRow((p) => ({ ...p, status: v }))}
                    ariaLabel="Toggle status"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsEditOpen(false)}
                  className={`px-6 py-2.5 rounded-xl border font-semibold transition-colors ${isDark ? 'border-white/10 text-gray-300 hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleEditSubmit}
                  className="px-6 py-2.5 rounded-xl text-white font-semibold transition-colors"
                  style={{ backgroundColor: currentTheme.color }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Data Table Section */}
      <div className={`${isDark ? 'bg-[#1e293b]/80 border-white/10' : 'bg-white/80 border-white/50'} backdrop-blur rounded-2xl shadow-xl border overflow-hidden`}>
        <div className="overflow-x-auto">
          {loading && rows.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No Lead APIs found. Add one to get started.</div>
          ) : (
            <table className="min-w-[1100px] w-full">
              <thead>
                <tr className="text-white" style={{ backgroundColor: currentTheme.color }}>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Lead Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Lead Source</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Lead Source Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Campaign</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Duplicate Permission</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Days Permission</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Actions</th>
                </tr>
              </thead>

              <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-200'}`}>
                {rows
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((r, idx) => (
                  <tr key={r.id} className={idx % 2 === 0 ? (isDark ? 'bg-[#1e293b]' : 'bg-white') : ''} style={{ backgroundColor: idx % 2 === 0 ? undefined : `${currentTheme.color}08` }}>
                    <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{r.leadCode}</td>
                    <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{r.leadSource}</td>
                    <td className="px-4 py-3">
                      <select
                        value={r.leadSourceType}
                        onChange={(e) => handleInlineUpdate(r.id, { leadSourceType: e.target.value })}
                        className={`w-[170px] h-9 px-3 border rounded-lg text-sm transition-all focus:outline-none ${isDark ? 'bg-[#0f172a] border-white/10 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                      >
                        {SOURCE_TYPE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </td>
                    <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-900'}`}>{r.campaign}</td>
                    <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-900'}`}>{r.category}</td>
                    <td className="px-4 py-3">
                      <Toggle
                        checked={r.duplicatePermission}
                        onChange={(v) => handleInlineUpdate(r.id, { duplicatePermission: v })}
                        ariaLabel="Toggle duplicate permission"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={r.daysPermission}
                        onChange={(e) => handleInlineUpdate(r.id, { daysPermission: e.target.value })}
                        className={`w-[140px] h-9 px-3 border rounded-lg text-sm transition-all focus:outline-none ${isDark ? 'bg-[#0f172a] border-white/10 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                      >
                        {DAYS_PERMISSION_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <Toggle
                        checked={r.status}
                        onChange={(v) => handleInlineUpdate(r.id, { status: v })}
                        ariaLabel="Toggle status"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => openEditModal(r)}
                          className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${isDark ? 'border-blue-800 bg-blue-900/20 text-blue-400 hover:bg-blue-900/40' : 'border-blue-100 bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                          title="Edit"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(r.id)}
                          className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${isDark ? 'border-red-800 bg-red-900/20 text-red-400 hover:bg-red-900/40' : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'}`}
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownloadRowData(r)}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors"
                          style={{ borderColor: `${currentTheme.color}40`, backgroundColor: `${currentTheme.color}10`, color: currentTheme.color }}
                          title="Download Data PDF"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-4 px-4 pb-4">
          <Pagination 
            currentPage={currentPage}
            totalItems={rows.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>
    </div>
  );
}
